// src/components/SendMessage.js
import React, { useState, useEffect, useRef } from 'react';
import {
    FormControl,
    TextField,
    Button,
    Typography,
    MenuItem,
    Box,
    List,
    ListItem,
    Paper,
    Chip,
    Stack,
    CircularProgress,
    Alert
} from '@mui/material';
import { sendMessage } from '../api/messageApi'; // Funktsioon, mis saadab sõnumi
import { searchUsers } from '../api/membersApi';  // Otsib kasutajaid
import { getGroups } from '../api/groupsApi';
import ReactQuill from 'react-quill'; // Import React Quill
import 'react-quill/dist/quill.snow.css'; // Import Quill styles

import { getEmailTemplates } from '../api/emailTemplateApi';

export default function SendMessage({
    affiliate,
    affiliateEmail,
    preSelectedUsers = [],
    preSelectedGuests = [],
    initialSubject = '',
    initialBody = '',
    onMessageSent,
    onSendClick
}) {
    const [subject, setSubject] = useState(initialSubject);
    const [body, setBody] = useState(initialBody);
    // recipientType is set based on what preSelected items are available
    const [recipientType, setRecipientType] = useState(() => {
        if (preSelectedUsers && preSelectedUsers.length > 0) return 'user';
        if (preSelectedGuests && preSelectedGuests.length > 0) return 'guest';
        return 'user';
    });
    const [recipient, setRecipient] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const [selectedUsers, setSelectedUsers] = useState(preSelectedUsers || []);
    const [selectedGuests, setSelectedGuests] = useState(preSelectedGuests || []);
    const [selectedGroupId, setSelectedGroupId] = useState(null);
    const [status, setStatus] = useState('');
    const [statusSeverity, setStatusSeverity] = useState('info');
    const [subjectError, setSubjectError] = useState(false);
    const [templates, setTemplates] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState('');
    const [sending, setSending] = useState(false);
    const selectMenuProps = {
        PaperProps: {
            sx: {
                zIndex: 2305
            }
        },
        sx: {
            zIndex: 2305
        }
    };

    // Use refs to keep track of current state values for the parent
    const stateRef = useRef({
        subject,
        body,
        selectedUsers,
        selectedGuests,
        recipientType,
        recipient,
        sending
    });

    // Update ref whenever state changes
    useEffect(() => {
        stateRef.current = {
            subject,
            body,
            selectedUsers,
            selectedGuests,
            recipientType,
            recipient,
            sending
        };
    }, [subject, body, selectedUsers, selectedGuests, recipientType, recipient, sending]);

    // Quill editor modules and formats configuration
    const modules = {
        toolbar: [
            [{ 'header': [1, 2, 3, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'color': [] }, { 'background': [] }],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            [{ 'align': [] }],
            ['link'],
            ['clean']
        ],
    };

    const formats = [
        'header',
        'bold', 'italic', 'underline', 'strike',
        'color', 'background',
        'list', 'bullet',
        'align',
        'link'
    ];

    // Load templates from API
    useEffect(() => {
        const loadTemplates = async () => {
            if (affiliate) {
                try {
                    const data = await getEmailTemplates(affiliate);
                    setTemplates(data);
                } catch (error) {
                    console.error('Error loading templates:', error);
                }
            }
        };
        loadTemplates();
    }, [affiliate]);

    // Keep pre-filled content in sync when modal/context changes
    useEffect(() => {
        setSubject(initialSubject || '');
        setSubjectError(false);
    }, [initialSubject]);

    useEffect(() => {
        setBody(initialBody || '');
    }, [initialBody]);

    // Kui "recipient" või "recipientType" muutub, teeme otsingu
    useEffect(() => {
        const fetchData = async () => {
            if (recipientType !== 'user' && recipientType !== 'group') {
                setSearchResults([]);
                setShowDropdown(false);
                return;
            }

            if (recipient.length < 1) {
                // Kui pole midagi sisestatud, peidame dropdowni ja tühjendame tulemused
                setSearchResults([]);
                setShowDropdown(false);
                return;
            }

            // Alates esimesest tähest proovime otsida
            if (recipientType === 'user') {
                try {
                    const results = await searchUsers(recipient);
                    // Tagastab massiivi: [{ id, fullName }, ...]
                    setSearchResults(results);
                    setShowDropdown(true);
                } catch (error) {
                    console.error('searchUsers error:', error);
                }
            } else if (recipientType === 'group') {
                // recipientType = 'group'
                try {
                    const groups = await getGroups(affiliate);
                    // getGroups toob kõik grupid. Filtreerime neid sisestuse alusel:
                    const filtered = groups.filter((g) =>
                        g.groupName.toLowerCase().includes(recipient.toLowerCase())
                    );
                    setSearchResults(filtered);
                    setShowDropdown(true);
                } catch (error) {
                    console.error('getGroups error:', error);
                }
            }
        };

        fetchData();
    }, [recipient, recipientType]);

    const handleSend = async (e) => {
        e.preventDefault();

        const currentState = stateRef.current;
        if (!currentState.subject || !currentState.subject.trim()) {
            setSubjectError(true);
            setStatusSeverity('error');
            setStatus('Subject is required.');
            return;
        }
        setSubjectError(false);

        setSending(true);
        setStatusSeverity('info');
        setStatus('Sending...');

        console.log('📧 handleSend called:', {
            selectedUsers: currentState.selectedUsers,
            selectedGuests: currentState.selectedGuests,
            recipientType: currentState.recipientType,
            subject: currentState.subject,
            body: currentState.body,
            usersCount: currentState.selectedUsers.length,
            guestsCount: currentState.selectedGuests.length
        });

        try {
            let totalRecipients = 0;

            // Send to selected users – ÜKS API KUTSE kõigi kasutajate jaoks
            if (currentState.recipientType === 'user' && currentState.selectedUsers.length > 0) {
                const recipientIds = currentState.selectedUsers.map((user) => user.id);

                console.log('📤 Sending to users:', recipientIds);

                const payload = {
                    senderId: affiliate,
                    subject: currentState.subject,
                    body: currentState.body,
                    recipientType: 'user',
                    affiliateEmail,
                    recipientIds,
                };

                await sendMessage(payload);
                totalRecipients += currentState.selectedUsers.length;
            }

            // Send to selected guests
            if (currentState.selectedGuests.length > 0) {
                const guestEmails = currentState.selectedGuests.map(guest => ({
                    email: guest.email,
                    name: `${guest.firstName} ${guest.lastName}`
                }));

                console.log('📤 Sending to guests:', guestEmails);

                const payload = {
                    senderId: affiliate,
                    subject: currentState.subject,
                    body: currentState.body,
                    recipientType: 'guest',
                    affiliateEmail,
                    recipientEmails: guestEmails
                };

                await sendMessage(payload);
                totalRecipients += currentState.selectedGuests.length;
            }

            if (totalRecipients > 0) {
                setStatusSeverity('success');
                setStatus(`Email successfully sent to ${totalRecipients} recipients!`);
            } else if (
                currentState.recipientType === 'group' ||
                currentState.recipientType === 'allMembers' ||
                currentState.recipientType === 'exMembers'
            ) {
                // Original logic for groups, all members and ex members
                const payload = {
                    senderId: affiliate,
                    subject: currentState.subject,
                    body: currentState.body,
                    recipientType: currentState.recipientType,
                    affiliateEmail,
                };

                if (currentState.recipientType === 'group') {
                    payload.groupName = currentState.recipient;
                }

                await sendMessage(payload);
                setStatusSeverity('success');
                setStatus('Email sent successfully!');
            } else {
                setStatusSeverity('error');
                setStatus('Please select at least one recipient!');
                return;
            }

            // Tühjendame väljad
            setSubject('');
            setBody('');
            setRecipient('');
            setSelectedUsers([]);
            setSelectedGuests([]);
            setSelectedGroupId(null);
            setSearchResults([]);
            setShowDropdown(false);

            // Call onMessageSent callback if provided
            if (onMessageSent) {
                onMessageSent();
            }
        } catch (error) {
            console.error('❌ Email saatmise viga:', error);
            setStatusSeverity('error');
            setStatus('Sending failed...');
        } finally {
            setSending(false);
        }
    };

    const handleSelectResult = (item) => {
        if (recipientType === 'user') {
            // Check if user is already in the selected users list
            if (!selectedUsers.some(user => user.id === item.id)) {
                setSelectedUsers([...selectedUsers, item]);
            }
            // Clear the search input and results
            setRecipient('');
            setSearchResults([]);
        } else {
            setRecipient(item.groupName);     // Kuvame inputis groupName
            setSelectedGroupId(item.id);      // Grupi ID, kui vaja
        }
        setShowDropdown(false);
    };

    const handleRemoveUser = (userId) => {
        setSelectedUsers(selectedUsers.filter(user => user.id !== userId));
    };

    const handleRemoveGuest = (guestId) => {
        setSelectedGuests(selectedGuests.filter(guest => guest.id !== guestId));
    };

    const handleTemplateSelect = (templateId) => {
        setSelectedTemplate(templateId);
        if (templateId) {
            const template = templates.find(t => t.id === templateId);
            if (template) {
                setSubject(template.subject);
                setBody(template.body);
            }
        }
    };

    // Check if send button is disabled
    const isSendDisabled = () => {
        return (
            sending ||
            (recipientType === 'user' && selectedUsers.length === 0 && selectedGuests.length === 0) ||
            (recipientType === 'guest' && selectedGuests.length === 0) ||
            (recipientType === 'group' && !recipient)
        );
    };

    // Expose handleSend to parent ONCE when component mounts
    useEffect(() => {
        if (onSendClick) {
            onSendClick({
                handleSend,
                sending,
                isDisabled: isSendDisabled()
            });
        }
        // Only run once on mount - ignore the exhaustive-deps warning
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div style={{ position: 'relative' }}>
            <Typography variant="h5" gutterBottom>
                Send Message
            </Typography>

            <form onSubmit={handleSend}>
                {/* Template selector */}
                {templates.length > 0 && (
                    <FormControl fullWidth margin="normal">
                        <TextField
                            select
                            label="Use Template (Optional)"
                            value={selectedTemplate}
                            onChange={(e) => handleTemplateSelect(e.target.value)}
                            helperText="Select a template to pre-fill the message"
                            SelectProps={{ MenuProps: selectMenuProps }}
                        >
                            <MenuItem value="">-- None --</MenuItem>
                            {templates.map((template) => (
                                <MenuItem key={template.id} value={template.id}>
                                    {template.name}
                                </MenuItem>
                            ))}
                        </TextField>
                    </FormControl>
                )}

                {/* Valik: user või group - peida kui on preSelected users või guests */}
                {!(preSelectedUsers?.length > 0 || preSelectedGuests?.length > 0) && (
                <FormControl fullWidth margin="normal">
                    <TextField
                        select
                        label="Send to"
                        value={recipientType}
                        onChange={(e) => {
                            setRecipientType(e.target.value);
                            // Resetime pooled asjad, kui tüüp muutub
                            setRecipient('');
                            setSearchResults([]);
                            setSelectedUsers([]);
                            setSelectedGuests([]);
                            setSelectedGroupId(null);
                            setShowDropdown(false);
                        }}
                        SelectProps={{ MenuProps: selectMenuProps }}
                    >
                        <MenuItem value="user">User</MenuItem>
                        <MenuItem value="group">Group</MenuItem>
                        <MenuItem value="allMembers">All Members (active plan or ended within last 6 months)</MenuItem>
                        <MenuItem value="exMembers">Ex Members (no plan for 30+ days)</MenuItem>
                    </TextField>
                </FormControl>
                )}

                {/* Sisend: kas fullName või groupName otsimiseks - peida kui on preSelected */}
                {!(preSelectedUsers?.length > 0 || preSelectedGuests?.length > 0) && (recipientType === 'user' || recipientType === 'group') && (
                <FormControl fullWidth margin="normal" sx={{ position: 'relative' }}>
                    <TextField
                        label={recipientType === 'user' ? 'Search User by Name' : 'Search Group by Name'}
                        value={recipient}
                        onChange={(e) => {
                            setRecipient(e.target.value);
                        }}
                        helperText={
                            recipientType === 'user'
                                ? "Type a user's name"
                                : 'Type a group name'
                        }
                    />
                    {/* Dropdown, mis avaneb, kui showDropdown=true ja searchResults on saadaval */}
                    {showDropdown && searchResults.length > 0 && (
                        <Paper
                            elevation={3}
                            sx={{
                                position: 'absolute',
                                top: '100%',
                                left: 0,
                                right: 0,
                                zIndex: 10,
                                maxHeight: 200,
                                overflowY: 'auto',
                            }}
                        >
                            <List>
                                {recipientType === 'user'
                                    ? searchResults.map((user) => (
                                        <ListItem
                                            button
                                            key={user.id}
                                            onClick={() => handleSelectResult(user)}
                                        >
                                            {user.fullName}
                                        </ListItem>
                                    ))
                                    : // recipientType = 'group'
                                    searchResults.map((group) => (
                                        <ListItem
                                            button
                                            key={group.id}
                                            onClick={() => handleSelectResult(group)}
                                        >
                                            {group.groupName}
                                        </ListItem>
                                    ))}
                            </List>
                        </Paper>
                    )}
                </FormControl>
                )}

                {!(preSelectedUsers?.length > 0 || preSelectedGuests?.length > 0) && recipientType === 'allMembers' && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 1 }}>
                        Message will be sent to members who have had an active plan within the last 6 months.
                    </Typography>
                )}

                {!(preSelectedUsers?.length > 0 || preSelectedGuests?.length > 0) && recipientType === 'exMembers' && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 1 }}>
                        Message will be sent to members who have had no plan for at least 30 days.
                    </Typography>
                )}

                {/* Display selected users and guests as chips */}
                {((recipientType === 'user' || recipientType === 'guest') && (selectedUsers.length > 0 || selectedGuests.length > 0)) && (
                    <Box sx={{ mt: 2, mb: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                            Selected Recipients:
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            {selectedUsers.map((user) => (
                                <Chip
                                    key={`user-${user.id}`}
                                    label={user.fullName}
                                    onDelete={() => handleRemoveUser(user.id)}
                                    sx={{ margin: '3px' }}
                                />
                            ))}
                            {selectedGuests.map((guest) => (
                                <Chip
                                    key={`guest-${guest.id}`}
                                    label={`${guest.firstName} ${guest.lastName} (Guest)`}
                                    onDelete={() => handleRemoveGuest(guest.id)}
                                    color="success"
                                    sx={{ margin: '3px' }}
                                />
                            ))}
                        </Stack>
                    </Box>
                )}

                {/* Pealkiri */}
                <FormControl fullWidth margin="normal">
                    <TextField
                        label="Subject"
                        value={subject}
                        error={subjectError}
                        helperText={subjectError ? 'Subject is required' : ''}
                        onChange={(e) => {
                            const nextValue = e.target.value;
                            setSubject(nextValue);
                            if (nextValue.trim()) {
                                setSubjectError(false);
                            }
                        }}
                    />
                </FormControl>

                {/* Rich Text Editor for body */}
                <FormControl fullWidth margin="normal">
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                        Message Body
                    </Typography>
                    <div style={{ border: '1px solid #ddd', borderRadius: '4px', marginBottom: '16px' }}>
                        <ReactQuill
                            theme="snow"
                            value={body}
                            onChange={setBody}
                            modules={modules}
                            formats={formats}
                            style={{ height: '250px', marginBottom: '40px' }}
                        />
                    </div>
                </FormControl>

                {/* Only show Send button if onSendClick is not provided (standalone mode) */}
                {!onSendClick && (
                    <Button
                        variant="contained"
                        type="submit"
                        disabled={isSendDisabled()}
                        startIcon={sending ? <CircularProgress size={20} sx={{ color: '#ffffff' }} /> : null}
                    >
                        {sending ? 'Sending...' : 'Send'}
                    </Button>
                )}
            </form>

            {status && (
                <Alert severity={statusSeverity} variant="filled" sx={{ mt: 2 }}>
                    {status}
                </Alert>
            )}
        </div>
    );
}